import {
  Button,
  Grid,
  TextField,
  Typography,
  FormLabel,
  Box,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
} from "@mui/material";
import { FastField, Form, Formik, Field, useField } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader";
import { CLIENTS_URL } from "../constants";
import { LanguageDropdown } from "./LanguageDropdown";
import { SUPPORTED_LANGUAGES } from "../../../constants";

export const REPORT_TYPE = {
  default: "default",
  special: "special",
  cockpit: "cockpit",
  culturlytics: "culturlytics",
  developmentCenter: "developmentCenter",
};

function InnerForm({ client, error, loading, editMode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [, , { setValue: setIsSpecialReport }] = useField({
    name: "isSpecialReport",
    value: !!client?.isSpecialReport,
  });
  const [{ value: reportType }, , { setValue: setReportType }] = useField({
    name: "reportType",
  });

  const handleReportTypeChange = (event, value) => {
    setIsSpecialReport(value === REPORT_TYPE.special);
    setReportType(value);
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={7}>
        <FastField
          name="name"
          as={TextField}
          required
          label={t("tool.clientForm.name", "Name")}
          fullWidth
          variant="outlined"
        />
      </Grid>
      <Grid item xs={7}>
        <FastField
          name="cssLink"
          as={TextField}
          label={t("tool.clientForm.cssLink", "CSS Link")}
          fullWidth
          variant="outlined"
        />
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <LanguageDropdown />
            <Box sx={{ margin: "24px 0" }}>
              <FormLabel>{t("tool.clientForm.favIcon", "Fav Icon")}</FormLabel>
              <Field
                variant="outlined"
                fullWidth
                name="favIconUrl"
                as={TextField}
                size="small"
              />
            </Box>
          </Grid>
          <Grid item xs={3}>
            {client && (
              <TextField
                label="Client ID"
                variant="outlined"
                defaultValue={client._id}
                fullWidth
                size="small"
                disabled={true}
              />
            )}
          </Grid>
        </Grid>
        <FormLabel>{t("tool.clientForm.report", "Report")}</FormLabel>
        <Box
          component={Grid}
          item
          xs={8}
          p="10px 15px"
          m={"0 0 15px"}
          border="1px solid #bfbfc1"
          borderRadius={1}
        >
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box component={Grid} container spacing={1} alignItems="center">
                <Grid item xs={12}>
                  <RadioGroup
                    name="reportRadioGroup"
                    row={true}
                    value={reportType}
                    onChange={handleReportTypeChange}
                  >
                    <FormControlLabel
                      value={REPORT_TYPE.default}
                      control={<Radio />}
                      label={t("tool.clientForm.default", "Default")}
                    />
                    <FormControlLabel
                      value={REPORT_TYPE.special}
                      control={<Radio />}
                      label={t("tool.clientForm.special", "Special")}
                    />
                    <FormControlLabel
                      value={REPORT_TYPE.cockpit}
                      control={<Radio />}
                      label={t("tool.clientForm.cockpit", "Cockpit")}
                    />
                    <FormControlLabel
                      value={REPORT_TYPE.culturlytics}
                      control={<Radio />}
                      label={t("tool.clientForm.culturlytics", "Culturlytics")}
                    />
                    <FormControlLabel
                      value={REPORT_TYPE.developmentCenter}
                      control={<Radio />}
                      label={t(
                        "tool.clientForm.developmentCenter",
                        "Development Center"
                      )}
                    />
                  </RadioGroup>
                </Grid>
                <Grid item xs={5}>
                  <Typography>
                    {t(
                      "tool.clientForm.minimumParticipants",
                      "Minimum Participants"
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <FastField
                    name="reportMinParticipants"
                    as={TextField}
                    fullWidth
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    label={t("tool.clientForm.featureFilter", "Feature Filter")}
                    control={
                      <FastField
                        type="checkbox"
                        as={Checkbox}
                        name="reportAttrFilters"
                      />
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    label={t(
                      "tool.clientForm.noIndividualReport",
                      "No Individual Report"
                    )}
                    control={
                      <FastField
                        type="checkbox"
                        as={Checkbox}
                        name="disableIndividualReport"
                      />
                    }
                  />
                </Grid>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box
                component={Grid}
                container
                spacing={1}
                pt="8px"
                alignItems="center"
              >
                <Grid item xs={2}>
                  <Typography>{t("tool.clientForm.css", "CSS")}</Typography>
                </Grid>
                <Grid item xs={10}>
                  <Field
                    variant="outlined"
                    fullWidth
                    name="reportCssUrl"
                    as={TextField}
                    size="small"
                    disabled={reportType === REPORT_TYPE.default}
                  />
                </Grid>
              </Box>
              <Box
                component={Grid}
                container
                spacing={1}
                pt="8px"
                alignItems="center"
              >
                <Grid item xs={2}>
                  <Typography>
                    {t("tool.clientForm.logoUrl", "Logo URL")}
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Field
                    variant="outlined"
                    fullWidth
                    name="logoUrl"
                    as={TextField}
                    size="small"
                  />
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              disabled={loading}
            >
              {editMode
                ? t("tool.addEdit.save", "Save")
                : t("tool.addEdit.create", "Create")}
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                navigate(CLIENTS_URL);
              }}
            >
              {t("tool.reportForm.close", "Close")}
            </Button>
          </Grid>
        </Grid>
        {error && (
          <Typography color="secondary">
            {error?.response?.data?.message ??
              t("tool.addEdit.unexpectedError", "Unexpected error occurred")}
          </Typography>
        )}
        {loading && <Loader />}
      </Grid>
    </Grid>
  );
}

function ClientForm({ client, onSubmit, error, loading, editMode }) {
  const navigate = useNavigate();

  return (
    <Formik
      initialValues={{
        name: "",
        languageId: SUPPORTED_LANGUAGES[0].id,
        cssLink: "",
        isSpecialReport: false,
        reportType: client?.isSpecialReport
          ? REPORT_TYPE.special
          : REPORT_TYPE.default,
        reportMinParticipants: 1,
        reportCssUrl: "",
        logoUrl: "",
        favIconUrl: "",
        disableIndividualReport: false,
        reportAttrFilters: false,
        ...(client &&
          Object.keys(client).reduce(
            (acc, key) => ({
              ...acc,
              [key]: client[key],
            }),
            {}
          )),
      }}
      onSubmit={async values => {
        const res = await onSubmit(values);

        if (res) {
          navigate(`${CLIENTS_URL}/${res._id}`);
        }
      }}
    >
      {() => {
        return (
          <Form>
            <InnerForm
              client={client}
              error={error}
              loading={loading}
              editMode={editMode}
            />
          </Form>
        );
      }}
    </Formik>
  );
}

export default ClientForm;
